/* eslint-disable max-lines */
import { track } from "jiffy-analytics";
import { track as rudderstackTrack } from "common/utils/rudderstack/tracker";
import EventClass from "common/utils/ui/event-class";
import { ProductsFilterBrandURLHelper } from "./products-filter-brand-url-helper";

// eslint-disable-next-line import/prefer-default-export
export class ProductFilterLinkClass extends EventClass {
  #linkSelector = "";

  #baseURL = "";

  #lastInputName = null;

  #didFilterChange = false;

  constructor(props) {
    super(props);

    this.#linkSelector = props.eventSelector;
  }

  static $input($target) {
    return $target.querySelector("input");
  }

  static updateCheckedState($target) {
    const $input = ProductFilterLinkClass.$input($target);
    if (!$input) return;

    const isChecked = $input.checked;
    if ($input.type === "checkbox") {
      if (isChecked) {
        $input.removeAttribute("checked");
        $input.checked = false;
      } else {
        $input.setAttribute("checked", "checked");
        $input.checked = true;
      }

      if ($input.dataset.presentValue && $input.value === $input.dataset.emptyValue) {
        $input.value = $input.dataset.presentValue;
      } else if ($input.dataset.emptyValue && $input.value === $input.dataset.presentValue) {
        $input.value = $input.dataset.emptyValue;
      }
    } else if ($input.type === "radio") {
      const radios = document.querySelectorAll(`input[name="${$input.name}"]`);
      radios.forEach($radio => {
        if ($radio === $input) {
          $radio.setAttribute("checked", "checked");
        } else {
          $radio.removeAttribute("checked");
        }
        $radio.checked = $radio === $input; // eslint-disable-line no-param-reassign
      });
    }
  }

  get $links() {
    return document.querySelectorAll(this.#linkSelector);
  }

  get #serializedQueryURL() {
    // Use the most recently clicked link's href as base, or current URL as fallback
    const baseURL = new URL(this.#baseURL || window.location.href);
    const currentURL = new URL(window.location.href);

    // Check if we're on a brand page URL - preserve query params from current URL
    if (ProductsFilterBrandURLHelper.isBrandPageURL(currentURL)) {
      return this.#buildBrandPageURL();
    }

    // Build query string URL first
    let queryStringURL = this.#buildQueryStringURL(baseURL);
    if (this.#shouldResetPage()) {
      queryStringURL = ProductFilterLinkClass.#removePageParam(queryStringURL);
    }

    // Check if we should convert query string page to brand page
    // (e.g., when going from multiple brands to single brand)
    const checkedFilters = this.#collectCheckedFilters();
    const brandFilters = checkedFilters.brand || [];

    // If exactly one brand, convert to brand page format
    if (brandFilters.length === 1) {
      const currentBrandFilters = currentURL.searchParams.getAll("brand");
      const shouldPreserveMultiBrandQuery =
        currentBrandFilters.length > 1 && this.#lastInputName !== "brand";
      if (shouldPreserveMultiBrandQuery) {
        return queryStringURL;
      }

      return ProductsFilterBrandURLHelper.convertToBrandPageURL(queryStringURL, checkedFilters);
    }

    return queryStringURL;
  }

  #buildBrandPageURL() {
    const currentURL = new URL(window.location.href);
    const checkedFilters = this.#collectCheckedFilters();
    const isCanadaStore = currentURL.pathname === "/ca" || currentURL.pathname.startsWith("/ca/");
    const storePrefix = isCanadaStore ? "/ca" : "";

    // Check if multiple brands are selected - if so, use query string format
    const brandFilters = checkedFilters.brand || [];
    if (
      brandFilters.length > 1 ||
      (brandFilters.length === 1 &&
        !ProductsFilterBrandURLHelper.isCurrentBrandPage(brandFilters[0]))
    ) {
      // When leaving a brand page URL (e.g. because the user selected multiple brands),
      // the current brand/category may be implied by the URL path but not represented
      // as checked inputs in the filter DOM. Ensure they are carried into the query URL.
      this.#addImplicitBrandPageFiltersToCheckedFilters(currentURL, checkedFilters);

      return this.#buildQueryStringURLFromCheckedFilters(
        `${storePrefix}/`,
        currentURL,
        checkedFilters
      );
    }

    const categoryFilters = [...(checkedFilters.category || []), ...(checkedFilters.style || [])];
    const pathname = this.#buildBrandPathname(currentURL, categoryFilters, checkedFilters);

    return this.#assembleFinalURL(pathname, currentURL, checkedFilters);
  }

  #collectCheckedFilters() {
    const filters = {};
    Array.from(this.$links).forEach($link => {
      const $input = ProductFilterLinkClass.$input($link);
      if (!$input) return;
      const forcedState = $link?.getAttribute("data-forced-state");
      const isChecked = forcedState || $input.checked;

      if (isChecked && $input.value !== "forced-empty") {
        filters[$input.name] = filters[$input.name] || [];
        filters[$input.name].push($input.value);
      }
    });
    return filters;
  }

  #addImplicitBrandPageFiltersToCheckedFilters(currentURL, checkedFilters) {
    const match = currentURL.pathname.match(/^\/(?:ca\/)?brands\/([^/]+)\/([^/]+)$/);
    if (!match) return;

    const [, brandSlug, brandPageSegment] = match;

    if (!checkedFilters.brand || checkedFilters.brand.length === 0) return;

    // If the current brand is already represented (even with different casing), don't add it again.
    const hasBrandAlready = checkedFilters.brand.some(
      brandValue => ProductsFilterBrandURLHelper.generateSlug(brandValue) === brandSlug
    );
    if (!hasBrandAlready) {
      const implicitBrandValue = this.#brandFilterValueForBrandSlug(brandSlug) || brandSlug;
      checkedFilters.brand = [...checkedFilters.brand, implicitBrandValue];
    }

    if (brandPageSegment === "all-products") return;

    const categoryName = ProductsFilterBrandURLHelper.getCategoryNameFromSlug(brandPageSegment);
    if (!categoryName) return;

    const existingCategories = checkedFilters.category || [];
    if (!existingCategories.includes(categoryName)) {
      checkedFilters.category = [...existingCategories, categoryName];
    }
  }

  #brandFilterValueForBrandSlug(brandSlug) {
    const brandInputs = Array.from(this.$links)
      .map($link => ProductFilterLinkClass.$input($link))
      .filter($input => $input?.name === "brand" && typeof $input.value === "string");

    const match = brandInputs.find(
      $input => ProductsFilterBrandURLHelper.generateSlug($input.value) === brandSlug
    );

    return match?.value || null;
  }

  #buildQueryStringURLFromCheckedFilters(pathname, currentURL, checkedFilters) {
    const url = new URL(pathname, window.location.origin);
    const shouldResetPage = this.#shouldResetPage();

    const filterParamNames = new Set(
      Array.from(this.$links)
        .map($link => ProductFilterLinkClass.$input($link)?.name)
        .filter(Boolean)
    );

    currentURL.searchParams.forEach((value, key) => {
      const shouldPreserveSearchParam = ProductFilterLinkClass.#shouldPreserveSearchParam(
        key,
        filterParamNames,
        { shouldResetPage }
      );
      if (shouldPreserveSearchParam) {
        url.searchParams.append(key, value);
      }
    });

    Object.entries(checkedFilters).forEach(([key, values]) => {
      values.forEach(value => url.searchParams.append(key, value));
    });

    return url.toString();
  }

  // eslint-disable-next-line class-methods-use-this
  #buildBrandPathname(currentURL, categoryFilters, checkedFilters) {
    const brandSlug = currentURL.pathname.match(/\/brands\/([^/]+)/)?.[1];
    const isCanadaStore = currentURL.pathname === "/ca" || currentURL.pathname.startsWith("/ca/");
    const storePrefix = isCanadaStore ? "/ca" : "";

    // If we're already on a brand category page, keep the existing SEO-friendly pathname
    // ONLY when we're not toggling category/style facets. If the user selects more than one
    // category/style, we must transition to /brands/:brand/all-products with query params.
    const brandPageMatch = currentURL.pathname.match(/^\/(?:ca\/)?brands\/[^/]+\/([^/]+)$/);
    const currentBrandPageSegment = brandPageMatch?.[1];
    const isBrandCategoryPage =
      Boolean(currentBrandPageSegment) && currentBrandPageSegment !== "all-products";
    if (isBrandCategoryPage && categoryFilters.length === 0) {
      const isRemovingCategory = ["category", "style"].includes(this.#lastInputName);
      if (!isRemovingCategory) {
        // Category/style are represented in the path already; don't duplicate them as query params.
        delete checkedFilters.category;
        delete checkedFilters.style;
        delete checkedFilters.brand;
        return currentURL.pathname;
      }
    }

    if (categoryFilters.length === 1 && brandSlug) {
      const categorySlug = ProductsFilterBrandURLHelper.getCategorySlug(categoryFilters[0]);
      if (categorySlug) {
        // When we navigate to a brand category page, the category (and brand) are represented
        // in the URL path already, so they must not be duplicated as query params.
        delete checkedFilters.category;
        delete checkedFilters.style;
        delete checkedFilters.brand;
        return `${storePrefix}/brands/${brandSlug}/${categorySlug}`;
      }
    }

    // On brand all-products pages, keep all filters in query params except brand (brand is in path).
    delete checkedFilters.brand;
    return `${storePrefix}/brands/${brandSlug}/all-products`;
  }

  #assembleFinalURL(pathname, currentURL, checkedFilters) {
    const url = new URL(pathname, window.location.origin);
    const shouldResetPage = this.#shouldResetPage();
    const filterParamNames = new Set(
      Array.from(this.$links)
        .map($link => ProductFilterLinkClass.$input($link)?.name)
        .filter(Boolean)
    );

    currentURL.searchParams.forEach((value, key) => {
      const shouldPreserveSearchParam = ProductFilterLinkClass.#shouldPreserveSearchParam(
        key,
        filterParamNames,
        {
          shouldResetPage,
          excludedKeys: new Set(["brand"])
        }
      );
      if (shouldPreserveSearchParam) {
        url.searchParams.append(key, value);
      }
    });

    Object.entries(checkedFilters).forEach(([key, values]) => {
      // Skip brand param as it's already in the URL path
      if (key !== "brand") {
        values.forEach(value => url.searchParams.append(key, value));
      }
    });

    return url.toString();
  }

  #buildQueryStringURL(baseURL) {
    const url = new URL(baseURL.pathname || "/", window.location.origin);
    const shouldResetPage = this.#shouldResetPage();

    // Get all filter param names that exist in the links
    const filterParamNames = new Set(
      Array.from(this.$links)
        .map($link => ProductFilterLinkClass.$input($link)?.name)
        .filter(Boolean)
    );

    // Preserve all params from base URL except filter params
    baseURL.searchParams.forEach((value, key) => {
      const shouldPreserveSearchParam = ProductFilterLinkClass.#shouldPreserveSearchParam(
        key,
        filterParamNames,
        { shouldResetPage }
      );
      if (shouldPreserveSearchParam) {
        url.searchParams.append(key, value);
      }
    });

    // Add all checked filter params (supports multiple values per param)
    Array.from(this.$links).forEach($link => {
      const $input = ProductFilterLinkClass.$input($link);
      if (!$input) return;
      const forcedState = $link?.getAttribute("data-forced-state");
      const flag = forcedState ? true : $input.checked;
      if (flag && $input.value !== "forced-empty") {
        url.searchParams.append($input.name, $input.value);
      }
    });

    return url.toString();
  }

  #shouldResetPage() {
    return this.#didFilterChange;
  }

  static #shouldPreserveSearchParam(
    key,
    filterParamNames,
    { shouldResetPage = false, excludedKeys = new Set() } = {}
  ) {
    if (excludedKeys.has(key)) return false;
    if (filterParamNames.has(key)) return false;
    if (shouldResetPage && key === "page") return false;
    return true;
  }

  // Reset page on any filter change to avoid stale pagination state.
  static #removePageParam(urlString) {
    const url = new URL(urlString, window.location.origin);
    url.searchParams.delete("page");
    return url.toString();
  }

  get #activeFilterItems() {
    return Array.from(this.$links).filter($link => {
      const $input = ProductFilterLinkClass.$input($link);
      return $input?.checked;
    });
  }

  manualSubmit() {
    this.#activeFilterItems.forEach(ProductFilterLinkClass.sendClickEvent);
    window.location.href = this.#serializedQueryURL;
  }

  event(event, $target) {
    super.event(event, $target);

    event.preventDefault();
    event.stopPropagation();

    // Store the clicked link's href as the base URL for building the final URL
    this.#baseURL = $target.href;
    this.#lastInputName = ProductFilterLinkClass.$input($target)?.name || null;
    this.#didFilterChange = true;

    ProductFilterLinkClass.updateCheckedState($target);
  }

  static sendClickEvent($target) {
    const { mixpanelEvent, mixpanelData } = $target.dataset;
    let parsedData;
    try {
      parsedData = JSON.parse(mixpanelData || "{}");
    } catch (error) {
      console.error("Failed to parse mixpanel data:", error);
      parsedData = {};
    }

    track(mixpanelEvent, parsedData);

    const $anyLineItem = document.querySelector(".js-item-details-tracker");
    const pageName = $anyLineItem?.dataset?.pageName;
    rudderstackTrack("Navigation Clicked", {
      destination: $target.href,
      is_app: Boolean(window.MOBILE_APP),
      location: pageName || window.location.pathname,
      platform: window.MOBILE_APP_PLATFORM || "web",
      text: parsedData.filter_name,
      type: "link",
      url: window.location.href,
      nav_type: "side_nav"
    });
  }
}
